import CensusFooter from "@/components/census/CensusFooter";
import CensusHeader from "@/components/census/CensusHeader";
import CensusAttending from "@/components/CensusAttending";
import CensusGrid from "@/components/CensusGrid";
import CensusGridAcuity from "@/components/CensusGridAcuity";
import CensusHandoff from "@/components/CensusHandoff";
import CensusList from "@/components/CensusList";
import CensusListAcuity from "@/components/CensusListAcuity";
import CensusListReview from "@/components/CensusListReview";
import ExternalSite from "@/components/popups/ExternalSite";
import LegendDescription from "@/components/popups/LegendDescription";
import Settings from "@/components/popups/Settings";
import { FILTER_MY_PATIENTS, UNIT_ALL } from "@/components/shared/util";
import { navigationNamespace } from "@/store/modules/navigation";
import { ViewConfig } from "@/store/modules/patient/config";
import { Patient } from "@/store/modules/patient/types";
import { userNamespace } from "@/store/modules/user";
import _ from "lodash";
import Vue from "vue";
import { Component, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";

const patientNamespace = { namespace: "patient" };

const CENSUS_POLLING_INTERVAL = 60000;
const CENSUS_FAST_POLLING_INTERVAL = 10000;

@Component({
  components: {
    CensusHeader,
    CensusFooter,
    CensusGrid,
    CensusListReview,
    CensusGridAcuity,
    CensusAttending,
    CensusHandoff,
    CensusList,
    CensusListAcuity,
    LegendDescription,
    Settings,
    ExternalSite
  }
})
export default class Census extends Vue {
  @Action("fetchCensus", patientNamespace)
  fetchCensus!: Function;

  @Action("clearCensus", patientNamespace)
  clearCensus!: Function;

  @Getter("isMask", userNamespace)
  isMask!: boolean;

  @Getter("patients", patientNamespace)
  patients!: Array<Patient>;

  @Getter("darkMode", navigationNamespace)
  dark!: boolean;

  @Getter("area", patientNamespace)
  area!: string;

  @Getter("vars", patientNamespace)
  vars!: any;

  @Getter("popupDescription", patientNamespace)
  popupDescription!: string;

  @Getter("selectedUnit", patientNamespace)
  selectedUnit!: string;

  @Getter("selectedView", patientNamespace)
  selectedView!: string;

  @Getter("selectedFilters", patientNamespace)
  selectedFilters!: string | null;

  @Getter("selectedSort", patientNamespace)
  selectedSort!: string;

  @Getter("views", patientNamespace)
  views!: ViewConfig[];

  @Getter("viewConfig", patientNamespace)
  viewConfig!: ViewConfig;

  @Getter("currentId", navigationNamespace)
  currentId!: string;

  @Getter("isWirelessTesting", userNamespace)
  isWirelessTesting!: boolean;

  @Getter("isTesting", patientNamespace)
  isTesting!: boolean;

  @Getter("censusLoaded", patientNamespace)
  censusLoaded!: boolean;

  interval: any = null;

  get patientCount() {
    return this.patients.length;
  }

  get loading() {
    return (
      (this.censusLoaded && this.patients.length === 0) || !this.censusLoaded
    );
  }

  get pollIntervalInMs() {
    return this.isWirelessTesting || this.isTesting
      ? CENSUS_FAST_POLLING_INTERVAL
      : CENSUS_POLLING_INTERVAL;
  }

  get showingPopup() {
    return this.$route.query.popupName;
  }

  noPatientsButtonConfig: Record<string, string | undefined> = {};
  get showNoPatientsButton() {
    return Object.keys(this.noPatientsButtonConfig).length !== 0;
  }

  get headerComponent() {
    return this.viewConfig && this.viewConfig.headerComponent;
  }

  @Watch("censusLoaded")
  loadCompleteWatcher() {
    if (this.censusLoaded) {
      this.startPolling();
      this.onResize();
      this.noPatientsButtonConfig = {};

      const params = this.$route.params;
      if (
        params.unit === UNIT_ALL &&
        params.filters &&
        params.filters.includes(FILTER_MY_PATIENTS)
      ) {
        // recommend removing my patients filter
        this.noPatientsButtonConfig.filters = params.filters
          .split(",")
          .filter(i => {
            i !== FILTER_MY_PATIENTS;
          })
          .join(",");
        // special case to remove filter from list
        if (this.noPatientsButtonConfig.filters === "") {
          this.noPatientsButtonConfig.filters = undefined;
        }
      } else if (params.unit !== UNIT_ALL) {
        // recommend going to all
        this.noPatientsButtonConfig.unit = UNIT_ALL;
      }
    }
  }

  reducePatientFilters() {
    // @ts-ignore Requires undefined to unset a param
    this.$router.push({ params: this.noPatientsButtonConfig });
  }

  get toPatientsButtonText() {
    let text = "";
    if (this.noPatientsButtonConfig.unit) {
      text = "Go to All units";
    } else if (
      this.noPatientsButtonConfig.filters ||
      this.noPatientsButtonConfig.filters === undefined
    ) {
      text = "Remove My Patients filter";
    }
    return text;
  }

  get noPatientsText() {
    if (
      this.$route.params.filters &&
      this.$route.params.filters.includes(FILTER_MY_PATIENTS)
    ) {
      return "You have no current patients";
    } else {
      return "No current patients";
    }
  }

  @Watch("currentId")
  idSelected(newId: string) {
    if (newId !== "") {
      this.$router.push({
        name: "body",
        params: {
          room: newId
        }
      });
    }
  }

  @Action("selectId", navigationNamespace)
  selectId!: Function;

  // Watch for route changes
  @Watch("$route.path")
  async onRouteChange() {
    this.stopPolling();
    this.clearCensus();
    // initial = true
    this.updateCensus();
  }

  created() {
    // clear out selected id
    this.selectId("");
    this.clearCensus();
  }

  @Watch("vars", { immediate: true })
  varsWatcher() {
    if (this.vars) {
      // initial = true
      this.updateCensus();
    }
  }

  beforeDestroy() {
    this.stopPolling();
  }

  get censusComponent() {
    const view = this.viewConfig;
    if (view.acuityComponent && this.selectedSort === "acuity") {
      return view.acuityComponent;
    } else {
      return view.component;
    }
  }

  private startPolling() {
    if (this.interval) {
      return;
    }

    this.interval = setInterval(() => {
      this.updateCensus();
    }, this.pollIntervalInMs);
  }

  private stopPolling() {
    clearInterval(this.interval);
    this.interval = null;
  }

  @Watch("isWirelessTesting")
  @Watch("isTesting")
  async changePollingInterval() {
    this.stopPolling();
    this.startPolling();
  }

  @Watch("isMask")
  updateCensus(): void {
    this.fetchCensus({
      view: this.selectedView,
      unit: this.selectedUnit,
      filtersAndSorting: {
        filters: this.selectedFilters,
        sortBy: this.selectedSort
      }
    });
  }

  @Watch("$vuetify.breakpoint", { deep: true })
  onResize() {
    // wait for reflow to finish
    requestAnimationFrame(() => {
      // look up footer height
      const bottomBarHeight = _.get(
        this.$refs,
        "footer.$el.clientHeight",
        0
      ) as number;

      const main = this.$el.querySelector("main");
      // Adjust main padding to match footer
      (<HTMLElement>main).style.paddingBottom = `${bottomBarHeight}px`;
    });
  }

  get fixedHeightView() {
    return this.selectedView === "multipatient";
  }
}
